import { useState } from 'react';

import { UploadedFile } from '@backrs_ui/common-ui';

import { DocumentFileUpload } from '@backrs_ui/common-ui/src/molecules/Profile/DocumentFileUpload';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  StoryDocument,
  useUpdateUserProfileMutation,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { getBackrsToken } from '@backrs_ui/grow-ui/src/utils/tokenHelpers';
import { uploadFile } from '@backrs_ui/grow-ui/src/utils/uploadFile';

import { Box, Theme, SxProps, Typography } from '@mui/material';

import { ProfileDocumentUploadModal } from './atoms/ProfileDocumentUploadModal';
import { FileUploadConfig } from './types/profile.types';

const PROFILE_FORM_DOCUMENT_STRINGS = appStrings.modules.profile.modalUploader.document;

type ProfileFormDocumentProps = Readonly<{
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  fileUploadConfig: FileUploadConfig;
}>;

export const ProfileFormDocument = ({
  onSuccess,
  onError,
  fileUploadConfig,
}: ProfileFormDocumentProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updateUserProfile] = useUpdateUserProfileMutation();
  const { user } = useAppSelector((state) => state.user);

  const [uploadedFileInfo, setUploadedFileInfo] = useState<{ file: File; mediaId: number } | null>(
    null,
  );
  const [inProgressFileUpload, setInProgressFileUpload] = useState<File | null>(null);

  const [modalOpen, setModalOpen] = useState(false);

  const handleFileSelect = async (file: File) => {
    setError(null);
    try {
      setIsUploading(true);
      setInProgressFileUpload(file);

      const token = getBackrsToken();

      if (!token) {
        throw new Error('No auth token found');
      }

      const result = await uploadFile({ file, token });

      setUploadedFileInfo({ file, mediaId: result.mediaId });

      if (fileUploadConfig.overrideModal) {
        if (!user?.id) {
          throw new Error('User ID is required');
        }

        // Make the API request directly when modal is overridden
        const profileData: Omit<StoryDocument, 'id' | 'media_url' | 'profile_option_answers_id'> = {
          user_id: user.id,
          category_id: fileUploadConfig.categoryId,
          question_id: fileUploadConfig.questionId,
          media_type: 'video',
          visibility: fileUploadConfig.visibility,
          option_id: fileUploadConfig.optionId as
            | 'resume'
            | 'transcript'
            | 'other'
            | 'personal_assessment'
            | null,
          media_id: result.mediaId,
          title: '',
          description: '',
        };

        await updateUserProfile({
          field: fileUploadConfig.key,
          value: profileData,
        }).unwrap();

        onSuccess?.();
        setUploadedFileInfo(null);
      } else {
        setModalOpen(true);
      }
    } catch (error) {
      onError?.(error as Error);
    } finally {
      setIsUploading(false);
      setInProgressFileUpload(null);
    }
  };

  const getFileSize = (file: File | undefined) => {
    if (!file) {
      return '';
    }

    if (file.size > 1024 * 1024) {
      return `${(file.size / 1024 / 1024).toFixed(2)} MB`;
    }

    return `${((file?.size || 0) / 1024).toFixed(2)} KB`;
  };

  return (
    <Box>
      <DocumentFileUpload
        key={fileUploadConfig.type}
        inputId={fileUploadConfig.type + '-uploader'}
        onFileSelect={handleFileSelect}
        onError={(error) => {
          onError?.(error);

          if (error.message.includes('File too large')) {
            setError(
              `File size exceeds maximum limit of ${fileUploadConfig.maxFileSize / 1024 / 1024}MB. Please try again.`,
            );
          }
        }}
        acceptedFileTypes={fileUploadConfig.acceptedFileTypes}
        maxFileSize={fileUploadConfig.maxFileSize}
      />

      {error && (
        <Typography
          color={({ backrs }) => backrs.colors['Colors/Text/text-error-primary']}
          variant="text-sm-regular"
        >
          {error}
        </Typography>
      )}

      {isUploading && (
        <Box sx={uploadingContainerSx}>
          <UploadedFile
            fileName={inProgressFileUpload?.name || ''}
            fileSize={`${PROFILE_FORM_DOCUMENT_STRINGS.uploading} (${getFileSize(
              inProgressFileUpload || undefined,
            )})`}
            fileType={inProgressFileUpload?.name.split('.').pop() || ''}
          />
        </Box>
      )}

      {uploadedFileInfo && !fileUploadConfig.overrideModal && user && (
        <ProfileDocumentUploadModal
          key={fileUploadConfig.type}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setUploadedFileInfo(null);
            setError(null);
          }}
          mediaType="document"
          mediaId={uploadedFileInfo.mediaId}
          file={uploadedFileInfo.file}
          onSuccess={onSuccess}
          onError={onError}
          modalTitle={fileUploadConfig.title}
          modalDescription={fileUploadConfig.description}
          icon="UploadCloud02"
          fileUploadConfig={fileUploadConfig}
          userId={user.id}
        />
      )}
    </Box>
  );
};

const uploadingContainerSx: SxProps<Theme> = {
  mt: 1,
};
